import * as React from 'react';
import { useCallback, useMemo, useState } from 'react';

import type { QueryResult } from '@apollo/client';
import { jwtDecode } from 'jwt-decode';

import { TASK_TERMINATED_STATUSES } from 'common/components/TasksMenu/TaskItem/TaskItem';
import type { TaskResponse } from 'common/components/TasksWidget/queries';
import { getTokenFromLocalStorage } from 'common/helpers/jwtHelper';

import type { SourceHelpers } from '../Source';
import Source from '../Source';
import type { MyTasksResponse } from './queries';
import { getMyTasks } from './queries';
import { myTaskCreated, myTaskUpdated } from './subscriptions';

export type MyTaskSourceProps = {
  onTaskTerminate?: (task: TaskResponse, helpers: SourceHelpers<TaskResponse>) => void;
  onTaskCreate?: (task: TaskResponse, helpers: SourceHelpers<TaskResponse>) => void;
  children: (
    entities: TaskResponse[],
    helpers: SourceHelpers<TaskResponse>,
    queryResult: QueryResult<MyTasksResponse>
  ) => React.ReactNode;
};

export type MyTaskSourceState = {
  userId?: string;
};

const selectEntityId = (task: TaskResponse) => task.id;
const selectSyncOn = (task: TaskResponse) => new Date(task.updatedAt);
const selectEntities = (response?: MyTasksResponse) => response?.tasks || [];
const selectStaleEntities = (response?: MyTasksResponse) => response?.sync || [];

function MyTaskSource(props: MyTaskSourceProps) {
  const { onTaskCreate, onTaskTerminate } = props;

  const [state] = useState<MyTaskSourceState>(() => {
    const token = getTokenFromLocalStorage();

    if (!token) return {};

    const { sub: userId } = jwtDecode<{ sub: string }>(token);

    return {
      userId,
    };
  });

  const handleMyTaskUpdated = useCallback(
    (newState: TaskResponse, prevState: TaskResponse, helpers: SourceHelpers<TaskResponse>) => {
      if (TASK_TERMINATED_STATUSES.includes(newState.status) && !TASK_TERMINATED_STATUSES.includes(prevState.status)) {
        helpers.deleteFromStore(newState);
      }
    },
    []
  );

  const handleMyTaskCreated = useCallback(
    (newState: TaskResponse, helpers: SourceHelpers<TaskResponse>) => {
      onTaskCreate?.(newState, helpers);
    },
    [onTaskCreate]
  );

  const handleMyTaskDeleted = useCallback(
    (deletedState: TaskResponse, helpers: SourceHelpers<TaskResponse>) => {
      onTaskTerminate?.(deletedState, helpers);
    },
    [onTaskTerminate]
  );

  const queryVariables = useMemo(() => ({ startedBy: state.userId }), [state.userId]);
  const subscriptions = useMemo(
    () => ({
      create: [{ query: myTaskCreated, variables: { startedBy: state.userId } }],
      update: { query: myTaskUpdated },
    }),
    [state.userId]
  );

  return (
    <Source<TaskResponse, MyTasksResponse>
      skip={!state.userId}
      name="MyTaskSource"
      onEntityCreated={handleMyTaskCreated}
      onEntityUpdated={handleMyTaskUpdated}
      onEntityDeleted={handleMyTaskDeleted}
      selectEntities={selectEntities}
      selectStaleEntities={selectStaleEntities}
      selectEntityId={selectEntityId}
      selectSyncOn={selectSyncOn}
      query={getMyTasks}
      queryVariables={queryVariables}
      subscriptions={subscriptions}
    >
      {(entities, helpers, queryResult) => {
        return props.children(entities, helpers, queryResult);
      }}
    </Source>
  );
}

export default MyTaskSource;
